import { useNotify, useRecordContext, useResourceContext, useUpdate } from "ra-core";
import { useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import type { CustomFieldDef } from "../types";

/**
 * Quick-add a custom property from any record page. The property definition
 * is universal - it lands in tenant configuration, so it immediately appears
 * on every record of this object type, in edit forms, the report builder,
 * and the CSV import mapper. The value entered here is saved on this record.
 */

const slugify = (label: string) =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const QuickAddProperty = ({
  object,
}: {
  object: CustomFieldDef["object"];
}) => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const record = useRecordContext();
  const resource = useResourceContext();
  const [update] = useUpdate();
  const notify = useNotify();

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomFieldDef["type"]>("text");
  const [value, setValue] = useState("");

  if (!record || !resource) return null;

  const existing = config.customFields ?? [];
  const key = slugify(label);
  const duplicate =
    key !== "" &&
    existing.some((f) => f.object === object && (f.key ?? "") === key);

  const save = () => {
    if (!key || duplicate) return;
    updateConfig({
      ...config,
      customFields: [...existing, { key, label: label.trim(), object, type }],
    });
    if (value.trim() !== "") {
      const existingCustomFields =
        (record as { custom_fields?: Record<string, unknown> }).custom_fields ?? {};
      update(resource, {
        id: record.id,
        data: {
          custom_fields: {
            ...existingCustomFields,
            [key]: type === "number" ? Number(value) : value.trim(),
          },
        },
        previousData: record,
      });
    }
    notify(`Property "${label.trim()}" added for all ${object}`, {
      type: "info",
    });
    setLabel("");
    setValue("");
    setType("text");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
        >
          <Plus className="w-3.5 h-3.5" /> Add property
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 flex flex-col gap-2" align="start">
        <p className="text-sm font-medium">New property</p>
        <p className="text-xs text-muted-foreground">
          Added for every {object.slice(0, -1)} - forms, reports, and imports
          pick it up automatically.
        </p>
        <Input
          className="h-8 text-sm"
          placeholder="Property name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoFocus
        />
        {duplicate && (
          <p className="text-xs text-destructive">
            A property with this name already exists.
          </p>
        )}
        <Select
          value={type}
          onValueChange={(t: CustomFieldDef["type"]) => setType(t)}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="date">Date</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="h-8 text-sm"
          type={type === "number" ? "number" : type === "date" ? "date" : "text"}
          placeholder="Value for this record (optional)"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button size="sm" disabled={!key || duplicate} onClick={save}>
          Add property
        </Button>
      </PopoverContent>
    </Popover>
  );
};
